<template>
  <div class="route-container" :class="{ selected: selected }">

    <div class="header-container">

      <div class="route-info">
        <status-badge v-if="route.status == constants.INVENTORY_STATUS_NEARING_CAPACITY" :status="constants.INVENTORY_STATUS_NEARING_CAPACITY" :inventory="route.inventory"></status-badge>
        <h2 class="travelTime">{{ route.departureTime }} - {{ route.arrivalTime }}</h2>
        <div class="duration"><span class="icon-duration"></span>&nbsp;<duration :hours="travelTime.hours" :minutes="travelTime.minutes"/></div>
        <div class="disruption-alert" v-if="activeDisruption"><span class="icon-alert"></span>&nbsp;{{ $t('form.label.p2p.disruption-message') }}</div>
        <div class="input-toggle details-toggle">
            <input :id="`details-toggle_${route.id}`" class="toggle-button" v-model="showDetails" type="checkbox">
            <label :for="`details-toggle_${route.id}`">{{ $t('general.label.details') }}</label>
        </div>
      </div>

      <div class="fares">
        <!-- Loop for fares and figure out styling for different number of fares -->
        <div class="single-fare">
          <div class="price">{{ $formatter.currency(route.price) }}</div>
          <p class="fare-type">Standard</p>
          <div class="button-container">
            <button class="button-book" :class="{ selected: selected }" v-on:click="select">{{ selected ? "Selected" : "Book" }}</button>
          </div>
        </div>
      </div>
    </div>

    <div class="details" :class="{hidden: !showDetails}">
      <div class="details-left">
        <div class="vehicle-features" v-if="route.equipmentDecorators && route.equipmentDecorators.length">
          <h2>{{ $t('form.label.p2p.vehicle-features') }}</h2>
          <!-- Loop for vehicle features -->
          <div class="decorator-container">
            <span v-tooltip.top="decoration.label" v-for="decoration in route.equipmentDecorators" :key="decoration.label" :class="decoration.class"></span>
          </div>
        </div>
        <div class="route-timeline">
          <h2>{{ $t('form.label.p2p.route-timeline') }}</h2>
          <div>
            <h3>{{ $formatter.time(route.routeTimeLine[0].startTime) }}</h3>
            <h3 class="location-name">{{ route.routeTimeLine[0].name }}</h3>
          </div>
          <div class="inner">
            <div class="input-toggle timeline-toggle">
              <input :id="`timeline-toggle_${route.id}`" class="toggle-button" v-model="showTimeline" type="checkbox">
              <label :for="`timeline-toggle_${route.id}`">{{ $tc("form.label.p2p.show-stops",route.routeTimeLine.length - 2) }}</label>
            </div>
            <div :class="{hidden: !showTimeline}">
              <div v-for="(location, index) in route.routeTimeLine" :key="index">
                <div v-if="index != 0 && index != route.routeTimeLine.length - 1">
                  {{ $formatter.time(location.startTime) }} - {{ location.name }}
                </div>
              </div>
            </div>
          </div>
          <div>
            <h3>{{ $formatter.time(route.routeTimeLine[route.routeTimeLine.length - 1].endTime) }}</h3>
            <h3 class="location-name">{{ route.routeTimeLine[route.routeTimeLine.length - 1].name }}</h3>
          </div>

          <!--
          <div class="routeLabel">{{ location.name }}</div>
          <div class="routeView"> @ {{ location.startTime }} - {{ location.endTime }} <span v-if="location.dayOffset">+{{ $tc("general.label.day-lowercase", location.dayOffset, { count: location.dayOffset }) }}</span></div>
          <div class="disruption-alert" v-if="location.disruptions.length"><span class="icon-alert"></span>&nbsp;{{ $t('form.label.p2p.disruption-message') }}</div>
          <div v-if="index < route.routeTimeLine.length - 1"> | </div> -->
        </div>
      </div>

      <div class="details-right">
        <h2>{{ $t('form.label.p2p.additional-info') }}</h2>
        <div class="additional-info">
          <h3 v-if="route.shortDescription" v-html="route.shortDescription"></h3>
          <p v-if="route.primaryDescription" v-html="route.primaryDescription"></p>
          <p v-if="route.additionalInfo" v-html="route.additionalInfo "></p>
          <p v-for="note in route.notesAndRestrictions" :key="note.id" v-html="note.name"></p>
        </div>
      </div>
    </div>

  </div>

</template>

<script>

import moment from 'moment';
import Duration from '@/components/Duration.vue';
import StatusBadge from '@/components/StatusBadge.vue';
import constants from '@/include/constants';

export default {
  name: 'TransportationRoute',
  props: {
    route: {
      type: Object
    },
    selected: {
      type: Boolean,
      default: false
    }
  },

  data(){
    return {
      showDetails: false,
      showTimeline: false,
      constants
    }
  },

  computed: {
    priceClass() {
      return this.route.promoEnabled ? 'price-original' : '';
    },

    travelTime() {
      let b = moment(this.route.arrivalTime, 'h:mm A');
      let a = moment(this.route.departureTime, 'h:mm A');
      let diff = b.diff(a, 'minutes');

      if (diff < 0) {
        // then it's next day.
        b = b.add(1, 'day');
        diff = b.diff(a, 'minutes');
      }

      var time = {hours: 0, minutes: 0};
      time.hours = Math.floor(diff / 60);
      time.minutes = diff - (60 * time.hours);
      return time;
    },

    activeDisruption(){
      return false;
    }
  },

  methods: {
    select(){
      this.$emit('select', this.route.id);
    }
  },

  components: {
    Duration,
    StatusBadge
  }
};
</script>

<style lang="scss">
.vue-tooltip-theme {
  background-color: $colorDarkGray;
  color: white;
  border-radius: 16px;
  padding: 5px 10px 4px;
  margin-bottom: 10px;
}
</style>

<style lang="scss" scoped>
.route-container {
  border: 1px solid $colorPaleGray;
  width: 100%;

  &.selected {
    border: 3px solid $colorBlue;
  }
}

h2 {
  font-size: 22px;
}

.header-container {
  height: 100%;
  min-height: 160px;
  background-color: $colorBgGray;
  position: relative;
}

.input-toggle {
  &.details-toggle {
    padding-top: 10px;
  }
}

.single-fare {
  text-align: center;
}

.price {
  padding-top: 5px;
  font-size: 21px;
  font-weight: 500;
}

.route-info {
  padding: 20px;
  height: 100%;
  position: relative;
  h1 {
   margin-bottom: 20px !important;
  }

  @include breakpoint(medium) {
    margin: 0;
    display: inline-block;
  }
  > :not(:first-child) {
    padding-top: 10px;
  }
}

.fares {
  border-left: none;
  border-top: 1px solid $colorPaleGray;
  padding: 20px;

  @include breakpoint(medium) {
    position: absolute;
    display: inline-block;
    max-width: 50%;
    min-width: 160px;
    height: 100%;
    border-left: 1px solid $colorPaleGray;
    border-top: none;
    right: 0;
  }
}

.fare-type {
  font-size: 16px;
  padding-top: 10px;
  color: $colorGray;
}

.details {
  overflow: hidden;
  transition: padding-top 0.15s linear;
  &.hidden {
    height: 0px;
    > * {
      display: none;
    }
  }

  &:not(.hidden) {
    min-height: 150px;
    overflow: auto;
    padding: 20px;
  }
}

.details-left {
  @include breakpoint(medium) {
    width: 40%;
    height: 100%;
    float: left;
  }
  > * {
    padding-bottom: 0;
  }
}

.details-right {
  float: left;
  height: 100%;
  h2 {
    margin-bottom: 15px;
  }
  @include breakpoint(medium) {
    width: 60%;
  }
}

.additional-info {
  > * {
    padding-bottom: 10px;
  }
}

.route-timeline {
  h2 {
    margin-bottom: 15px;
  }
  * {
    margin-bottom: 2px;
  }
  * h3 {
    margin-bottom: 0;
  }
  * .location-name {
    color: $colorBlue;
    padding-top: 2px;
  }

  > .inner {
    color: $colorGray;
    padding: 5px 0 10px 10px;

    > div.hidden {
      display: none;
    }

    > span {
      padding-left: 15px;
    }
  }
}

.disruption-alert {
  padding-top: 10px;
  color: $colorRed;
  padding-bottom: 30px;
  @include breakpoint(medium) {
    padding-bottom: 0;
  }
}

.down-arrow {
  @include font-awesome($fa-var-arrow-down);
}

.timeline-toggle {
  padding: 0;
}

.decorator-container {
  padding-top: 10px;
  > * {
    padding-right: 15px;
    font-size: 1.5em;
  }
}

.addition-info {
  > p {
    margin-bottom: 10px;
  }
}

.button-container {
  padding-top: 14px;
}

.button-book {
  padding-top: 10px;
  @include button($colorCTA, #fff);

  &.selected {
    @include button($colorGreen, #fff);
  }
}
</style>